import { useCallback, useEffect } from 'react';

export const useChatbot = (
    customerID: string,
    context: Record<string, unknown>,
    userId: string | undefined,
    mercuryApiKey: string,
    jestWorkerId: string | undefined,
) => {
    const scriptAlreadyExists = useCallback(() => document.querySelector('script#mercury-widget-snippet') !== null, []);

    const freezeProp = useCallback((target: object, propertyName: string) => {
        if (!jestWorkerId) {
            const descriptor = Object.getOwnPropertyDescriptor(target, propertyName);
            if (descriptor !== undefined) {
                const set = (): void => void 0;
                const get = (): ReturnType<typeof descriptor['value']> => descriptor.value;
                Object.defineProperty(target, propertyName, { enumerable: true, set, get, configurable: false });
            }
        }
    }, []);

    const appendScript = useCallback(() => {
        const generatedUserId =
            Math.random()
                .toString(36)
                .substring(2, 15) +
            Math.random()
                .toString(36)
                .substring(2, 15);

        const config = {
            userId: userId || generatedUserId,
            context,
        };

        const scriptWithGlobalData = document.createElement('script');
        scriptWithGlobalData.type = 'text/javascript';
        scriptWithGlobalData.text = `window.mercuryUser = ${JSON.stringify(config)};`;
        const script = document.createElement('script');
        script.type = 'application/javascript';
        script.src = 'https://cdn.mercury.ai/widget/v4/widget.js';
        script.async = true;
        script.id = 'mercury-widget-snippet';
        script.setAttribute('data-key', mercuryApiKey || '');
        document.body.appendChild(script);
        document.body.appendChild(scriptWithGlobalData);
    }, [customerID, context]);

    useEffect(() => {
        if (customerID && context && !scriptAlreadyExists()) {
            appendScript();
            freezeProp(window, 'mercuryUser');
        }
    }, [scriptAlreadyExists, appendScript, freezeProp, context, customerID]);

    const cleanupScript = () => {
        const widgetElement = document.getElementById('mercury-widget');
        widgetElement?.remove();

        const widgetScript = document.querySelector('script#mercury-widget-snippet');
        const dataScript = document.querySelector('script#mercury-widget-data');

        widgetScript?.remove();
        dataScript?.remove();
    };

    useEffect(() => {
        return () => {
            cleanupScript();
        };
    }, []);
};
